import React from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

type StepIconProps = {
  isActive: boolean;
  isCompleted: boolean;
  readOnly: boolean;
};

const StepIcon = ({ isActive, isCompleted, readOnly }: StepIconProps) => {
  if (isActive && readOnly) {
    return <Icons.CheckOld customClass="size-4" />;
  }

  if (isActive) {
    return <Icons.EditPencil customClass="w-4 h-4" />;
  }

  if (isCompleted) {
    return <Icons.CheckOld customClass="w-4 h-4 text-navy-500" />;
  }

  return <Icons.CircleDotted customClass="w-4 h-4 text-navy-500" />;
};

type Step = {
  value: string;
  label: string;
  isDisabled?: boolean;
};

type AidStudioStepperProps = {
  steps: Step[];
  activeStep: string;
  onStepChange?: (value: string) => void;
  completedSteps?: string[];
  readOnly?: boolean;
};

const AidStudioStepper = ({
  steps,
  activeStep,
  onStepChange,
  completedSteps = [],
  readOnly = false,
}: AidStudioStepperProps) => {
  const activeIndex = steps.findIndex((s: Step) => s.value === activeStep);

  const handleStepClick = (step: Step) => {
    if (!step.isDisabled && onStepChange) {
      onStepChange(step.value);
    }
  };

  return (
    <nav
      className="flex gap-2 items-center py-1 rounded-md"
      data-testid="stepper"
      aria-label="Progress"
    >
      {steps.map((step: Step, index: number) => {
        const isActive = activeStep === step.value;
        const isCompleted =
          completedSteps.includes(step.value) || index < activeIndex;
        const isDisabled = step.isDisabled && !isCompleted;
        const isClickable = !isDisabled && onStepChange;
        const isLast = index === steps.length - 1;
        const stepStateLabel = isActive
          ? 'current step'
          : isCompleted
          ? 'completed'
          : 'not started';
        const stepAriaLabel = `${step.label}, ${stepStateLabel}`;

        const labelClass = classNames(
          'text-base leading-6 whitespace-nowrap text-center',
          {
            'font-semibold text-navy-800': isActive,
            'font-normal text-navy-500': !isActive,
          }
        );

        const stepContent = (
          <>
            <StepIcon
              isActive={isActive}
              isCompleted={isCompleted}
              readOnly={readOnly}
            />
            <span className={labelClass}>{step.label}</span>
          </>
        );

        return (
          <React.Fragment key={step.value}>
            {isClickable ? (
              <button
                type="button"
                onClick={() => handleStepClick(step)}
                className="flex gap-1 items-center bg-transparent border-none p-0 cursor-pointer hover:opacity-80"
                data-testid={`step-${step.value}`}
                aria-current={isActive ? 'step' : undefined}
                aria-label={stepAriaLabel}
              >
                {stepContent}
              </button>
            ) : (
              <div
                className="flex gap-1 items-center"
                data-testid={`step-${step.value}`}
                aria-current={isActive ? 'step' : undefined}
                aria-disabled={isDisabled ? true : undefined}
                aria-label={stepAriaLabel}
              >
                {stepContent}
              </div>
            )}
            {!isLast && (
              <Icons.ChevronRight customClass="w-3.5 h-3.5 text-navy-200 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default AidStudioStepper;
